/**
 * One pass from an Instagram DM row to everything the inbox needs to render it
 * — `normalizeIgMessage()`.
 *
 * The Instagram twin of `whatsapp-message.ts`, and the same rationale: message
 * rendering used to be an if-chain in `instagram.inbox.tsx` sitting on top of
 * five local `JSON.parse` helpers that duplicated the ones inside
 * `messenger-content.tsx` — the two channels share Meta's Messenger Platform
 * payload format, so the parsing was the same code twice. The wire reading now
 * lives in `meta-message-payload.ts`; this module turns it into an
 * Instagram-specific view.
 *
 * Pure and React-free so the type matrix is unit-testable.
 */
import {
  buttonLink,
  firstAttachment,
  genericElements,
  buttonTemplate as readButtonTemplate,
  parseMetaPayload,
  productIds,
  quickReplyTitles,
  referral as readReferral,
  storyReply as readStoryReply,
} from './meta-message-payload';

/** Which renderer in the registry draws this message's body. */
export type IgMessageKind =
  | 'text'
  | 'media'
  | 'sticker'
  | 'quick_replies'
  | 'cards'
  | 'buttons'
  | 'share'
  | 'ephemeral'
  | 'reaction'
  | 'deleted'
  | 'referral_marker'
  | 'unsupported';

export type IgMediaKind = 'image' | 'video' | 'audio' | 'file';

export interface IgMediaView {
  kind: IgMediaKind;
  /** Durable mirrored URL when available, else Meta's expiring CDN URL. */
  url: string | null;
  /** True while only the expiring CDN URL is available — it may 404 at any
   *  time, which is why the mirror worker exists. */
  ephemeralUrl: boolean;
}

export interface IgCardView {
  title: string | null;
  subtitle: string | null;
  imageUrl: string | null;
  buttons: { title: string; url: string | null }[];
}

/** A post / reel / story / product share, which Instagram renders as a labelled
 *  attachment card rather than an inline player. */
export interface IgShareView {
  label: string;
  url: string | null;
  imageUrl: string | null;
}

export interface IgReferralView {
  source?: string;
  ref?: string;
  adTitle?: string;
  photoUrl?: string;
}

/** Context strips drawn above the body: an ad chip, a quoted reply, a
 *  "replied to your story" header. Independent of the body renderer. */
export interface IgContextView {
  referral: IgReferralView | null;
  replyTo: { id: number; preview: string; isIncoming: boolean } | null;
  storyReply: { url?: string } | null;
}

export interface IgCapabilities {
  /** Renders centred and chrome-less rather than as a bubble. */
  marker: boolean;
  /** Has one or more context strips above the body. */
  context: boolean;
  media: boolean;
}

export interface IgMessageView {
  kind: IgMessageKind;
  /** The Meta attachment type behind a `media` / `share` kind, when there was
   *  one (`story_mention`, `ig_reel`, `share`, …). */
  subtype: string | null;
  /** Always non-empty — the guaranteed-renderable line for this message. */
  fallbackLabel: string;
  /** Text to draw in the bubble. Null when media or a card carries the message. */
  body: string | null;
  media: IgMediaView | null;
  sticker: { url: string | null } | null;
  quickReplies: string[] | null;
  cards: IgCardView[] | null;
  buttons: { text: string | null; buttons: { title: string; url: string | null }[] } | null;
  share: IgShareView | null;
  reactionEmoji: string | null;
  /** For a standalone `messaging_referrals` marker row. */
  referralMarker: IgReferralView | null;
  context: IgContextView;
  capabilities: IgCapabilities;
}

/** The subset of the Instagram message DTO the normalizer reads. */
export interface IgMessageInput {
  message: string | null;
  messageType: string | null;
  payloadJson?: string | null;
  mediaUrl?: string | null;
  replyTo?: { id: number; preview: string; isIncoming: boolean } | null;
}

const isHttpUrl = (s: string | null | undefined): boolean => !!s && /^https?:\/\//i.test(s);

function trimmed(v: string | null | undefined): string | null {
  const t = v?.trim();
  return t ? t : null;
}

/**
 * Attachment type -> how Instagram presents it.
 *
 * Inline players for real media; a labelled card for the "shared something on
 * Instagram" types, which have no playable asset in the DM payload. Adding a
 * type Meta introduces later is one row here.
 */
const MEDIA_ATTACHMENTS: Record<string, IgMediaKind> = {
  image: 'image',
  video: 'video',
  audio: 'audio',
  file: 'file',
};

const SHARE_LABELS: Record<string, string> = {
  story_mention: 'Mentioned you in a story',
  story_reply: 'Replied to your story',
  ig_reel: 'Shared a reel',
  reel: 'Shared a reel',
  ig_post: 'Shared a post',
  share: 'Shared a post',
  media_share: 'Shared a post',
};

/** Fallback line for a message with nothing else to show. Never empty — this is
 *  what makes a blank bubble impossible. */
const SOFT_NOTE = '📎 Sent a message — open in Instagram';

export function normalizeIgMessage(m: IgMessageInput): IgMessageView {
  const payload = parseMetaPayload(m.payloadJson);
  const messageType = m.messageType;
  const text = trimmed(m.message);

  const context: IgContextView = {
    referral: messageType === 'referral' ? null : readReferral(payload),
    replyTo: m.replyTo ?? null,
    storyReply: readStoryReply(payload),
  };

  const base = {
    subtype: null as string | null,
    body: null as string | null,
    media: null as IgMediaView | null,
    sticker: null as { url: string | null } | null,
    quickReplies: null as string[] | null,
    cards: null as IgCardView[] | null,
    buttons: null as IgMessageView['buttons'],
    share: null as IgShareView | null,
    reactionEmoji: null as string | null,
    referralMarker: null as IgReferralView | null,
    context,
  };

  const finish = (
    kind: IgMessageKind,
    fields: Partial<typeof base> & { fallbackLabel?: string },
  ): IgMessageView => {
    const { fallbackLabel, ...rest } = fields;
    const merged = { ...base, ...rest };
    return {
      kind,
      ...merged,
      fallbackLabel: fallbackLabel ?? SOFT_NOTE,
      capabilities: {
        marker: kind === 'referral_marker',
        context: !!(merged.context.referral || merged.context.replyTo || merged.context.storyReply),
        media: merged.media !== null,
      },
    } as IgMessageView;
  };

  // A standalone messaging_referrals row — a centred "started from an ad"
  // marker, not a message with a body.
  if (messageType === 'referral') {
    const r = readReferral(payload);
    const label =
      r?.source === 'ADS'
        ? `Started from an Instagram ad${r.adTitle ? ` · ${r.adTitle}` : ''}`
        : 'Started from a referral link';
    return finish('referral_marker', { referralMarker: r, fallbackLabel: label, body: label });
  }

  if (messageType === 'deleted') {
    return finish('deleted', { fallbackLabel: 'Message deleted', body: 'Message deleted' });
  }

  if (messageType === 'reaction') {
    return finish('reaction', { reactionEmoji: text ?? '👍', fallbackLabel: text ?? '👍' });
  }

  if (messageType === 'sticker') {
    const url = m.mediaUrl ?? firstAttachment(payload)?.payload?.url ?? null;
    return finish('sticker', { sticker: { url } });
  }

  if (messageType === 'quick_replies') {
    return finish('quick_replies', { quickReplies: quickReplyTitles(payload), body: text });
  }

  if (messageType === 'generic_template') {
    const cards: IgCardView[] = genericElements(payload).map((el) => ({
      title: el.title ?? null,
      subtitle: el.subtitle ?? null,
      imageUrl: el.image_url ?? null,
      buttons: (el.buttons ?? []).map((b) => ({ title: b.title ?? '', url: buttonLink(b) })),
    }));
    return finish('cards', { cards, fallbackLabel: 'Shared a card' });
  }

  if (messageType === 'button_template') {
    const b = readButtonTemplate(payload);
    return finish('buttons', {
      buttons: {
        text: trimmed(b?.text) ?? text,
        buttons: (b?.buttons ?? []).map((btn) => ({
          title: btn.title ?? '',
          url: buttonLink(btn),
        })),
      },
      fallbackLabel: trimmed(b?.text) ?? text ?? SOFT_NOTE,
    });
  }

  if (messageType === 'media_share') {
    return finish('share', {
      share: { label: 'Shared a post', url: null, imageUrl: null },
      fallbackLabel: 'Shared a post',
    });
  }

  if (messageType === 'product_template') {
    const n = productIds(payload).length;
    const label = n > 1 ? `Shared ${n} products` : 'Shared a product';
    return finish('share', {
      share: { label, url: null, imageUrl: null },
      fallbackLabel: label,
    });
  }

  const att = firstAttachment(payload);
  if (att?.type) {
    // Prefer the durable mirrored URL; fall back to the expiring CDN URL.
    const mirrored = m.mediaUrl ?? null;
    const url = mirrored ?? att.payload?.url ?? null;
    const mediaKind = MEDIA_ATTACHMENTS[att.type];
    if (mediaKind) {
      return finish('media', {
        subtype: att.type,
        media: { kind: mediaKind, url, ephemeralUrl: mirrored === null && url !== null },
      });
    }
    const shareLabel = SHARE_LABELS[att.type];
    if (shareLabel) {
      return finish('share', {
        subtype: att.type,
        share: { label: shareLabel, url, imageUrl: url },
        fallbackLabel: shareLabel,
      });
    }
    if (att.type === 'ephemeral') {
      const label = 'Disappearing media (unavailable)';
      return finish('ephemeral', { subtype: att.type, fallbackLabel: label, body: label });
    }
  }

  // Real (non-URL) text, else the soft note. A bare CDN URL in `message` is
  // plumbing that leaked from an attachment row — never show it as the body.
  if (text && !isHttpUrl(text)) return finish('text', { body: text, fallbackLabel: text });
  return finish('unsupported', {});
}

/** One-line plain-text summary of an Instagram message — for reply previews and
 *  anywhere else a message is quoted. Never empty. */
export function igPreviewText(m: IgMessageInput): string {
  const view = normalizeIgMessage(m);
  return view.body ?? view.share?.label ?? view.reactionEmoji ?? view.fallbackLabel;
}
